import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { patrolApi } from "./api";
import { useOpenShift } from "@/features/shifts/hooks";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/shared/apiClient";
import { extractErrorMessage } from "@/shared/utils/errors";
import { isTransportError } from "@/shared/utils/networkErrors";
import type { PatrolScanResult, PatrolStatus } from "./types";
import { enqueuePatrolScan, loadPatrolScanOutbox } from "./offline/patrolScanOutbox";
import { patrolShiftIdForApi } from "@/features/shifts/offline/offlineShiftPlaceholders";
import { logger } from "@/shared/logger";

export function usePatrolConfig() {
  return useQuery({
    queryKey: ["patrol", "config"],
    queryFn: () => patrolApi.getPatrolConfig(),
    networkMode: "offlineFirst",
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function usePatrolStatus() {
  return useQuery({
    queryKey: ["patrol", "status"],
    queryFn: () => patrolApi.getPatrolStatus(),
    networkMode: "offlineFirst",
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function usePatrolOutboxRecords() {
  return useQuery({
    queryKey: ["patrol", "outbox"],
    queryFn: () => loadPatrolScanOutbox(),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function usePostPatrolScan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload, shiftId }: { payload: string; shiftId?: number }) =>
      patrolApi.postPatrolScan(payload, shiftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patrol", "config"] });
      queryClient.invalidateQueries({ queryKey: ["patrol", "status"] });
      queryClient.invalidateQueries({ queryKey: ["patrol", "outbox"] });
    },
  });
}

/** Shared submit logic: POST scan, navigate to success or show error toasts. Use from Shifts page or anywhere. */
export function usePatrolScanSubmit() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const postScan = usePostPatrolScan();
  const { data: openShift } = useOpenShift();
  const queryClient = useQueryClient();

  const submitPayload = useCallback(
    async (payload: string) => {
      if (postScan.isPending) return;
      logger.info("patrol.scan.submit.start", {
        payloadPreview: payload.slice(0, 40),
        hasOpenShift: !!openShift,
        openShiftId: openShift?.id,
      });
      try {
        const result = await postScan.mutateAsync({
          payload,
          shiftId: patrolShiftIdForApi(openShift ?? undefined),
        });
        logger.info("patrol.scan.submit.success", {
          qrPayload: result.qr_code?.payload,
          scannedAt: result.scanned_at,
        });
        navigate("/patrol/scan/success", { state: { scanResult: result } });
      } catch (error) {
        logger.warn("patrol.scan.submit.error", { error });
        if (error instanceof ApiError) {
          if (error.status === 403) {
            toast({
              title: "Scan not allowed",
              description: "You can't scan at this location.",
              variant: "destructive",
            });
            return;
          }
          if (error.status === 404) {
            toast({
              title: "Invalid QR code",
              description: "This QR code wasn't found.",
              variant: "destructive",
            });
            return;
          }
          if (error.status === 409) {
            const body = error.responseBody as { error?: { code?: string; message?: string } } | undefined;
            const isDuplicate = body?.error?.code === "PATROL_SCAN_NOT_UNIQUE";
            const message = isDuplicate
              ? body?.error?.message ?? "This QR code was already scanned in this patrol period. Each code counts once per period."
              : error.message;
            navigate("/patrol/scan/failed", {
              state: { duplicateScan: { message, payload } },
            });
            return;
          }
        }
        if (isTransportError(error)) {
          logger.warn("patrol.scan.submit.transportDetected", {
            payloadPreview: payload.slice(0, 40),
          });
          const patrolStatus = queryClient.getQueryData<PatrolStatus | null>(["patrol", "status"]);
          const clientRecordedAt = new Date().toISOString();
          await enqueuePatrolScan({
            payload,
            shiftId: patrolShiftIdForApi(openShift ?? undefined),
            clientRecordedAt,
            windowStart: patrolStatus?.window_start,
            windowEnd: patrolStatus?.window_end,
          });
          queryClient.invalidateQueries({ queryKey: ["patrol", "outbox"] });
          logger.info("patrol.scan.submit.queuedOffline", {
            clientRecordedAt,
            shiftId: patrolShiftIdForApi(openShift ?? undefined),
            windowStart: patrolStatus?.window_start,
            windowEnd: patrolStatus?.window_end,
          });
          const offlineResult: PatrolScanResult = {
            offlinePending: true,
            scanned_at: clientRecordedAt,
            qr_code: {
              id: 0,
              name: "Pending sync",
              description: null,
              payload,
            },
          };
          toast({
            title: "Scan successful",
            description: "Checkpoint recorded successfully",
          });
          navigate("/patrol/scan/success", { state: { scanResult: offlineResult } });
          return;
        }
        const message = extractErrorMessage(error);
        toast({
          title: "Scan failed",
          description: message || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    },
    [postScan, openShift?.id, navigate, toast, queryClient]
  );

  return { submitPayload, isPending: postScan.isPending };
}
